import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Save, Info } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle, faCircle, faInfoCircle, faList } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';
import {
  fetchSystemCaregiverById,
  updateSystemCaregiver,
  createSystemCaregiver,
  invalidateSystemCaregiverListCache,
  setCurrentSystemCaregiver
} from '../../../store/slices/systemCaregiverSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import practiceService from '../../../services/practiceService';
import { isEvitalsMonitoringPractice } from '../../../utils/practiceMonitoringUtils';
import { matchesWordStart } from '../../../utils/searchMatch';
import './AddSystemCaregiver.css';
import '../../PracticeManagement/PatientManagement/AddPatient/AddPatientModal.css';
import '../../../components/Common/InactivationConfirmModal/InactivationConfirmModal.css';
import { validatePassword, applyPasswordServerFieldError, formatDisplayError } from '../../../utils/passwordValidation';
import AddFormModel from '../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../utils/constants';
import useFocusTrap from '../../../hooks/useFocusTrap';
import { createModalEscapeKeyDownHandler } from '../../../utils/modalEscape';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../utils/phoneUtils';
import PhoneNumberInput from '../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../hooks/useUsernameEmailAvailability';
import FormFieldError from '../../../components/Common/FormFieldError/FormFieldError';
import ReassignmentModal from '../../../components/Common/ReassignmentModal/ReassignmentModal.jsx';
import {
  batchReassignPatientsToSystemCaregiver,
  buildEmptyReassignments,
  buildReassignmentsForAll,
  fetchActiveSystemCaregiversForReassignment,
  fetchPatientsForSystemCaregiver,
  getSystemCaregiverPatientName,
  hasUnassignedReassignments
} from '../../../utils/staffReassignmentFlow';

/** Matches validatePassword() — only these count as special characters. */
const PASSWORD_SPECIAL_CHAR_PATTERN = /[!@#$%^&*]/;

function emptySystemCaregiverFormState() {
  return {
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    status: 'Active',
    timeZone: ''
  };
}

const AddSystemCaregiverModal = ({ isOpen, onClose, onSave, practiceId: propPracticeId, isEditMode: propIsEditMode, editId: propEditId, isSystemAdmin: propIsSystemAdmin, isViewMode: propIsViewMode = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editId = propEditId || searchParams.get("editId");
  const isViewMode = Boolean(propIsViewMode);
  const isEditMode = !isViewMode && (propIsEditMode || Boolean(editId));
  const isRecordMode = isViewMode || isEditMode;
  const [showEventLogsModal, setShowEventLogsModal] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showEventLogsModal, logOverlayRef, logContainerRef);

  const getEventLogsPath = () => {
    if (!editId) return null;
    const basePath = `/event-logs/System%20Caregiver/${editId}`;
    const practiceId = propPracticeId || searchParams.get('practiceId');
    return practiceId ? `${basePath}?practiceId=${practiceId}` : basePath;
  };

  const { currentSystemCaregiver } = useSelector(state => state.systemCaregiver);
  const excludeUserId = isRecordMode ? (currentSystemCaregiver?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [practices, setPractices] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [showPracticeDropdown, setShowPracticeDropdown] = useState(false);
  const [practiceSearchTerm, setPracticeSearchTerm] = useState('');
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [selectedPracticeForModal, setSelectedPracticeForModal] = useState(null);
  const [practicePatients, setPracticePatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const practiceDropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showReassignmentModal, setShowReassignmentModal] = useState(false);
  const [assignedPatients, setAssignedPatients] = useState([]);
  const [alternativeCaregivers, setAlternativeCaregivers] = useState([]);
  const [reassignments, setReassignments] = useState({});
  const [isReassigning, setIsReassigning] = useState(false);

  // Focus trap - modal is always open when rendered
  const modalRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (practiceDropdownRef.current && !practiceDropdownRef.current.contains(event.target)) {
        setShowPracticeDropdown(false);
      }
    };

    if (showPracticeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPracticeDropdown]);

  useEffect(() => {
    if (!showPracticeDropdown) {
      setPracticeSearchTerm('');
    }
  }, [showPracticeDropdown]);

  const [formData, setFormData] = useState(emptySystemCaregiverFormState);

  const [initialState, setInitialState] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialState) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;

    const current = {
      formData: { ...formData, password: '', confirmPassword: '' },
      selectedPractices: selectedPractices.map(p => ({ practiceId: p.practiceId, patientIds: p.patientIds }))
    };

    return JSON.stringify(current) !== JSON.stringify(initialState);
  }, [formData, selectedPractices, isEditMode, initialState]);

  const [errors, setErrors] = useState({});

  const resetModalState = React.useCallback(() => {
    setFormData(emptySystemCaregiverFormState());
    setErrors({});
    setSelectedPractices([]);
    setInitialState(null);
    setIsSubmitting(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordCriteria(false);
    setPracticeSearchTerm('');
    setShowPracticeDropdown(false);
    setShowPatientModal(false);
    setSelectedPracticeForModal(null);
    setPracticePatients([]);
    setSelectedPatientIds([]);
    dispatch(setCurrentSystemCaregiver(null));
  }, [dispatch]);

  // Modal stays mounted while hidden — clear form and validation when closed or opened for add
  useEffect(() => {
    if (!isOpen) {
      resetModalState();
    }
  }, [isOpen, resetModalState]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      resetModalState();
    }
  }, [isOpen, isEditMode, resetModalState]);

  const toSafeTrimmedString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  // Fetch caregiver data if in edit mode
  useEffect(() => {
    if (!isOpen) return;
    if (isRecordMode && editId) {
      // Clear any stale caregiver data first so the skeleton shows until fresh data arrives
      dispatch(setCurrentSystemCaregiver(null));
      dispatch(fetchSystemCaregiverById(editId));
    }
  }, [isOpen, isRecordMode, editId, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'System Caregivers', path: '/system-caregiver-dashboard' },
      { label: isViewMode ? 'View System Caregiver' : isEditMode ? 'Edit Caregiver' : 'Add Caregiver', path: null, active: true }
    ]);
  }, [isEditMode, isViewMode, updateBreadcrumbs]);

  // Populate form when caregiver data is loaded
  useEffect(() => {
    if (isRecordMode && currentSystemCaregiver) {
      const stateId = currentSystemCaregiver.address?.state_id || currentSystemCaregiver.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: currentSystemCaregiver.title_id ? String(currentSystemCaregiver.title_id) : '',
        firstName: currentSystemCaregiver.first_name || '',
        middleInitial: currentSystemCaregiver.middle_name || '',
        lastName: currentSystemCaregiver.last_name || '',
        username: currentSystemCaregiver.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentSystemCaregiver.address?.address_line_1 || currentSystemCaregiver.address_line_1 || '',
        addressLine2: currentSystemCaregiver.address?.address_line_2 || currentSystemCaregiver.address_line_2 || '',
        city: currentSystemCaregiver.address?.city || currentSystemCaregiver.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: String(currentSystemCaregiver.address?.zip_code ?? currentSystemCaregiver.zip_code ?? ''),
        homePhone: formatPhoneNumber(currentSystemCaregiver.cell_phone || ''),
        cellPhone: formatPhoneNumber(currentSystemCaregiver.phone || ''),
        fax: formatPhoneNumber(currentSystemCaregiver.fax || ''),
        email: currentSystemCaregiver.email || '',
        twoWayAuth: (currentSystemCaregiver.is_two_way_auth === 1 || currentSystemCaregiver.is_two_way_auth === true) ? 'Enabled' : 'Disabled',
        status: currentSystemCaregiver.status ? (currentSystemCaregiver.status.charAt(0).toUpperCase() + currentSystemCaregiver.status.slice(1).toLowerCase()) : 'Active',
        timeZone: normalizeTimeZone(currentSystemCaregiver.time_zone || currentSystemCaregiver.timeZone || currentSystemCaregiver.timezone) || ''
      };

      setFormData(mappedData);
      setLoadedCredentials({
        username: mappedData.username || '',
        email: mappedData.email || '',
      });

      // Populate practices
      let practicesBatch = [];
      if (currentSystemCaregiver.practices && Array.isArray(currentSystemCaregiver.practices)) {
        practicesBatch = currentSystemCaregiver.practices
          .filter(isEvitalsMonitoringPractice)
          .map(p => ({
            practiceId: p.id,
            practiceName: p.name || p.practice_name || `Practice ${p.id}`,
            patientIds: p.patient_ids || []
          }));
        setSelectedPractices(practicesBatch);
      }

      setInitialState({
        formData: mappedData,
        selectedPractices: practicesBatch.map(p => ({ practiceId: p.practiceId, patientIds: p.patientIds }))
      });
    }
  }, [isRecordMode, currentSystemCaregiver]);

  // Fetch practices on component mount
  useEffect(() => {
    const fetchPractices = async () => {
      try {
        setLoadingPractices(true);
        const response = await practiceService.getPractices({ limit: 1000, evitals_monitoring_only: 1 });

        let practicesList = [];
        if (response && response.data) {
          if (response.data.success && response.data.data) {
            if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
              practicesList = response.data.data.practices;
            } else if (Array.isArray(response.data.data)) {
              practicesList = response.data.data;
            }
          } else if (Array.isArray(response.data)) {
            practicesList = response.data;
          }
        }

        practicesList = practicesList.filter(isEvitalsMonitoringPractice);

        setPractices(practicesList);
      } catch (error) {
        console.error('Error fetching practices:', error);
        toast.error('Failed to load practices');
      } finally {
        setLoadingPractices(false);
      }
    };

    fetchPractices();
  }, []);

  // Handle practice click - open patient selection modal only when practice has patients
  const handlePracticeClick = async (practice) => {
    setShowPracticeDropdown(false);
    setLoadingPatients(true);
    setPracticePatients([]);
    setSelectedPatientIds([]);

    try {
      const response = await practiceService.getPracticePatients(practice.id, { limit: 1000 });

      let patientsList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.patients && Array.isArray(response.data.data.patients)) {
            patientsList = response.data.data.patients;
          } else if (Array.isArray(response.data.data)) {
            patientsList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          patientsList = response.data;
        }
      }

      if (!patientsList.length) {
        // No patients — assign practice without opening the empty modal
        const practiceSelection = {
          practiceId: practice.id,
          practiceName: practice.practice_name || `Practice ${practice.id}`,
          patientIds: []
        };
        const existingIndex = selectedPractices.findIndex((sp) => sp.practiceId === practiceSelection.practiceId);
        if (existingIndex >= 0) {
          const updated = [...selectedPractices];
          updated[existingIndex] = practiceSelection;
          setSelectedPractices(updated);
        } else {
          setSelectedPractices([...selectedPractices, practiceSelection]);
        }
        setErrors((prev) => {
          if (!prev.practices) return prev;
          const next = { ...prev };
          delete next.practices;
          return next;
        });
        toast.info('No patients found for this practice. Practice assigned without patient selection.');
        return;
      }

      const existing = selectedPractices.find((sp) => sp.practiceId === practice.id);
      setSelectedPracticeForModal(practice);
      setPracticePatients(patientsList);
      setSelectedPatientIds(existing?.patientIds ? [...existing.patientIds] : []);
      setShowPatientModal(true);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to load patients for this practice');
    } finally {
      setLoadingPatients(false);
    }
  };

  // Handle patient selection in modal
  const handlePatientToggle = (patientId) => {
    setSelectedPatientIds(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  // Handle select all patients
  const handleSelectAllPatients = () => {
    if (selectedPatientIds.length === practicePatients.length) {
      setSelectedPatientIds([]);
    } else {
      setSelectedPatientIds(practicePatients.map(p => p.id));
    }
  };

  // Save practice with selected patients
  const handleSavePracticeSelection = () => {
    if (!selectedPracticeForModal) return;

    const practiceSelection = {
      practiceId: selectedPracticeForModal.id,
      practiceName: selectedPracticeForModal.practice_name || `Practice ${selectedPracticeForModal.id}`,
      patientIds: selectedPatientIds
    };

    const existingIndex = selectedPractices.findIndex(sp => sp.practiceId === practiceSelection.practiceId);
    if (existingIndex >= 0) {
      const updated = [...selectedPractices];
      updated[existingIndex] = practiceSelection;
      setSelectedPractices(updated);
    } else {
      setSelectedPractices([...selectedPractices, practiceSelection]);
    }

    setErrors((prev) => {
      if (!prev.practices) return prev;
      const next = { ...prev };
      delete next.practices;
      return next;
    });

    // Close modal and reset
    setShowPatientModal(false);
    setSelectedPracticeForModal(null);
    setSelectedPatientIds([]);
    setPracticePatients([]);
  };

  // Remove practice selection (at least one practice must remain)
  const handleRemovePractice = (practiceId) => {
    if (selectedPractices.length <= 1) {
      toast.warn('At least one practice must remain assigned.');
      return;
    }
    setSelectedPractices(selectedPractices.filter(sp => sp.practiceId !== practiceId));
    setErrors((prev) => {
      if (!prev.practices) return prev;
      const next = { ...prev };
      delete next.practices;
      return next;
    });
  };

  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.2 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      updateFieldErrorOnChange(setErrors, 'state', value);
      return;
    }

    // Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
      if (value.length > 20) {
        return;
      }
    }

    // Password Validation (Real-time)
    if (name === 'password') {
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value.trim(), userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // Email Validation (Real-time)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      return;
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    if (name === 'status' && isEditMode) {
      const currentStatus = formData.status;
      if (currentStatus === 'Active' && value === 'Inactive') {
        setShowConfirmationModal(true);
        return;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const zipCodeValue = toSafeTrimmedString(formData.zipCode);

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }
    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';

    // Password Validation
    const trimmedPassword = formData.password?.trim() || '';
    const trimmedConfirmPassword = formData.confirmPassword?.trim() || '';
    const userData = { firstName: formData.firstName, lastName: formData.lastName };

    if (!isEditMode) {
      if (!trimmedPassword) {
        newErrors.password = 'Password is required';
      } else {
        const validation = validatePassword(trimmedPassword, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (trimmedPassword) {
      const validation = validatePassword(trimmedPassword, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !trimmedConfirmPassword) newErrors.confirmPassword = 'Confirm Password is required';

    if (trimmedPassword && trimmedPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state || formData.state === 'Select' || !formData.state?.trim()) newErrors.state = 'State is required';

    if (!zipCodeValue) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (zipCodeValue.length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    if (!selectedPractices.length) {
      newErrors.practices = 'At least one practice is required';
    }

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  const getCurrentSystemCaregiverDisplayName = () => {
    const lastName = currentSystemCaregiver?.last_name || formData.lastName || '';
    const firstName = currentSystemCaregiver?.first_name || formData.firstName || '';
    return `${lastName}, ${firstName}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A';
  };

  const startInactivationFlow = async () => {
    setShowConfirmationModal(false);
    setIsSubmitting(true);
    try {
      const patients = await fetchPatientsForSystemCaregiver(editId);
      const alternatives = await fetchActiveSystemCaregiversForReassignment(editId, {
        patients
      });
      setAssignedPatients(patients);
      setAlternativeCaregivers(alternatives);

      if (patients.length > 0) {
        setReassignments(buildEmptyReassignments(patients));
        setShowReassignmentModal(true);
      } else {
        setFormData((prev) => ({ ...prev, status: 'Inactive' }));
        toast.info('No patients assigned. You can now save the system caregiver as Inactive.');
      }
    } catch (err) {
      console.error('Inactivation flow failed:', err);
      toast.error('Failed to initiate reassignment workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchReassign = async () => {
    setIsReassigning(true);
    try {
      if (hasUnassignedReassignments(assignedPatients, reassignments)) {
        toast.warning('Please select a new system caregiver for all patients');
        setIsReassigning(false);
        return;
      }

      await batchReassignPatientsToSystemCaregiver(editId, reassignments);
      setShowReassignmentModal(false);
      setFormData((prev) => ({ ...prev, status: 'Inactive' }));
      toast.success('Patients reassigned. Status updated to Inactive.');
    } catch (err) {
      console.error('Batch reassignment failed:', err);
      toast.error(formatDisplayError(err) || 'Failed to reassign patients');
    } finally {
      setIsReassigning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    if (!usernameOk || !emailOk) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    if (selectedPractices.length === 0) {
      toast.error('Please select at least one practice');
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedPassword = formData.password?.trim() || '';

      const systemCaregiverData = {
        title_id: parseInt(formData.title) || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        middle_name: formData.middleInitial,
        email: formData.email,
        phone: (formData.cellPhone || '').replace(/\D/g, ''),
        cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
        fax: formData.fax,
        address_line_1: formData.addressLine1,
        address_line_2: formData.addressLine2,
        city: formData.city,
        state_id: formData.stateId || 1,
        zip_code: formData.zipCode,
        username: formData.username,
        time_zone: formData.timeZone,
        ...(trimmedPassword ? { password: trimmedPassword } : {}),
        status: formData.status,
        is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
        practice_ids: selectedPractices.map(sp => sp.practiceId),
        practice_patient_assignments: selectedPractices.map(sp => ({
          practice_id: sp.practiceId,
          patient_ids: sp.patientIds || []
        }))
      };

      if (isEditMode) {
        await dispatch(updateSystemCaregiver({ systemCaregiverId: editId, systemCaregiverData })).unwrap();
        toast.success('System Caregiver updated successfully!');
      } else {
        await dispatch(createSystemCaregiver(systemCaregiverData)).unwrap();
        toast.success('System Caregiver created successfully!');
      }
      dispatch(invalidateSystemCaregiverListCache());
      if (onSave) onSave();
      else if (onClose) onClose();
    } catch (error) {
      console.error('Error saving system caregiver:', error);
      const apiMsg = formatDisplayError(error);
      const lower = apiMsg.toLowerCase();
      let hasUserErr = false;
      let hasEmailErr = false;

      if (lower.includes('username') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken. Please choose a different username.' }));
        hasUserErr = true;
      }
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email.' }));
        hasEmailErr = true;
      }

      if (applyPasswordServerFieldError(setErrors, apiMsg)) {
        return;
      }

      let friendlyMsg = '';
      if (hasUserErr && hasEmailErr) {
        friendlyMsg = 'This username is already taken and this email address is already registered. Please use different values.';
      } else if (hasUserErr) {
        friendlyMsg = 'This username is already taken. Please choose a different username.';
      } else if (hasEmailErr) {
        friendlyMsg = 'This email address is already registered. Please use a different email.';
      } else {
        friendlyMsg = apiMsg || `Failed to ${isEditMode ? 'update' : 'add'} system caregiver. Please try again.`;
      }
      toast.error(friendlyMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    resetModalState();
    if (onClose) onClose();
    else navigate(-1);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const closePatientModal = () => {
    setShowPatientModal(false);
    setSelectedPracticeForModal(null);
    setSelectedPatientIds([]);
    setPracticePatients([]);
  };

  const handleEscape = () => {
    if (showPatientModal) {
      closePatientModal();
      return;
    }
    if (showPracticeDropdown) {
      setShowPracticeDropdown(false);
      return;
    }
    handleModalClose();
  };

  const handleEscapeKeyDown = createModalEscapeKeyDownHandler(handleEscape);

  useFocusTrap(modalRef, isOpen, handleEscape);

  if (!isOpen) return null;

  if (isRecordMode && !currentSystemCaregiver) {
    return (
      <>
        <div className="pif-modal-overlay"></div>
        <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
          <div className="pif-container" style={{ padding: '30px' }}>
            <FormDetailSkeleton titleLines={1} fieldRows={9} columns={2} showFooter={false} />
            <FormDetailSkeleton titleLines={1} fieldRows={6} columns={1} showFooter={true} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pif-modal-overlay"></div>
      <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
        <div className="pif-container">
          <div className="pif-stepper-header">
            <button className="pif-modal-close-btn" type="button" onClick={handleModalClose}><X size={20} /></button>
            <h2 className="pif-stepper-title">{isViewMode ? "View System Caregiver" : isEditMode ? "Edit System Caregiver" : "Add System Caregiver"}</h2>
          </div>

          <div className="pif-content-wrapper">
            <form onSubmit={handleSubmit} noValidate>
              <fieldset disabled={isViewMode} className="pif-view-fieldset">
                <div className="pif-content">
                  <div className="pif-aligned-grid">

                    {/* Personal Info */}
                    <div className="pif-demo-section-label">Personal Information</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-title-group">
                          <label htmlFor="title">Title</label>
                          <select id="title" name="title" className={`pif-input ${errors.title ? "is-invalid" : ""}`} value={formData.title} onChange={handleInputChange}>
                            <option value="">-</option>
                            {TITLES && TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          {errors.title && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.title}</span>}
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="firstName">First Name <span className="text-red-500">*</span></label>
                          <input type="text" id="firstName" name="firstName" className={`pif-input ${errors.firstName ? "is-invalid" : ""}`} value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                          <FormFieldError message={errors.firstName} />
                        </div>
                      </div>
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-mi-group">
                          <label htmlFor="middleInitial">Middle Name</label>
                          <input type="text" id="middleInitial" name="middleInitial" className={`pif-input ${errors.middleInitial ? "is-invalid" : ""}`} value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} />
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="lastName">Last Name <span className="text-red-500">*</span></label>
                          <input type="text" id="lastName" name="lastName" className={`pif-input ${errors.lastName ? "is-invalid" : ""}`} value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                          <FormFieldError message={errors.lastName} />
                        </div>
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="homePhone">Home Phone</label>
                        <PhoneNumberInput id="homePhone" name="homePhone" className={`pif-input ${errors.homePhone ? "is-invalid" : ""}`} value={formData.homePhone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.homePhone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.homePhone}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="cellPhone">Cell Phone <span className="text-red-500">*</span></label>
                        <PhoneNumberInput id="cellPhone" name="cellPhone" className={`pif-input ${errors.cellPhone ? "is-invalid" : ""}`} value={formData.cellPhone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.cellPhone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.cellPhone}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="fax">Fax</label>
                        <PhoneNumberInput id="fax" name="fax" className={`pif-input ${errors.fax ? "is-invalid" : ""}`} value={formData.fax} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.fax && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.fax}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="username">Username <span className="text-red-500">*</span></label>
                        <input type="text" id="username" name="username" className={`pif-input ${errors.username ? "is-invalid" : ""}`} value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} />
                        <FormFieldError message={errors.username} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="email">Email <span className="text-red-500">*</span></label>
                        <input type="email" id="email" name="email" className={`pif-input ${errors.email ? "is-invalid" : ""}`} value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., caregiver@example.com" required />
                        <FormFieldError message={errors.email} />
                      </div>
                    </div>

                    {!isViewMode && (
                      <div className="pif-form-row-balanced">
                        <div className="pif-form-group">
                          <label htmlFor="password">Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showPassword ? "text" : "password"} id="password" name="password" className={`pif-input ${errors.password ? "is-invalid" : ""}`} value={formData.password} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" onFocus={() => setShowPasswordCriteria(true)} onBlur={() => setShowPasswordCriteria(false)} style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={togglePasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                            {showPasswordCriteria && (
                              <div className="ap-password-criteria-block">
                                <div className={`ap-criteria-item ${formData.password.length >= 8 ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={formData.password.length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                                </div>
                                <div className={`ap-criteria-item ${/[A-Z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[A-Z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[a-z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[a-z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[0-9]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[0-9]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                                </div>
                                <div className={`ap-criteria-item ${PASSWORD_SPECIAL_CHAR_PATTERN.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={PASSWORD_SPECIAL_CHAR_PATTERN.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character (!@#$%^&*)
                                </div>
                              </div>
                            )}
                          </div>
                          {errors.password && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.password}</span>}
                        </div>
                        <div className="pif-form-group">
                          <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showConfirmPassword ? "text" : "password"} id="confirmPassword" name="confirmPassword" className={`pif-input ${errors.confirmPassword ? "is-invalid" : ""}`} value={formData.confirmPassword} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={toggleConfirmPasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                            </button>
                          </div>
                          {errors.confirmPassword && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
                        </div>
                      </div>
                    )}

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine1" style={{ marginBottom: 0 }}>Address Line 1 <span className="text-red-500">*</span></label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine1?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine1" name="addressLine1" maxLength={250} className={`pif-input ${errors.addressLine1 ? "is-invalid" : ""}`} value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
                        <FormFieldError message={errors.addressLine1} />
                      </div>
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine2" style={{ marginBottom: 0 }}>Address Line 2</label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine2?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine2" name="addressLine2" maxLength={250} className={`pif-input ${errors.addressLine2 ? "is-invalid" : ""}`} value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="city">City <span className="text-red-500">*</span></label>
                        <input type="text" id="city" name="city" className={`pif-input ${errors.city ? "is-invalid" : ""}`} value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                        <FormFieldError message={errors.city} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="state">State <span className="text-red-500">*</span></label>
                        <select id="state" name="state" className={`pif-input ${errors.state ? "is-invalid" : ""}`} value={formData.state} onChange={handleInputChange} required>
                          <option value="">- Select State -</option>
                          {US_STATES && US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <FormFieldError message={errors.state} />
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="zipCode">Zip Code <span className="text-red-500">*</span></label>
                        <input type="text" id="zipCode" name="zipCode" className={`pif-input ${errors.zipCode ? "is-invalid" : ""}`} value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                        <FormFieldError message={errors.zipCode} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="timeZone">Time Zone <span className="text-red-500">*</span></label>
                        <select id="timeZone" name="timeZone" className={`pif-input ${errors.timeZone ? "is-invalid" : ""}`} value={formData.timeZone} onChange={handleInputChange} required>
                          <option value="">Select Time Zone</option>
                          {TIME_ZONES && TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                        {errors.timeZone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.timeZone}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="status">Status <span className="text-red-500">*</span></label>
                        <select id="status" name="status" className={`pif-input ${errors.status ? "is-invalid" : ""}`} value={formData.status} onChange={handleInputChange}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                        <FormFieldError message={errors.status} />
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="twoWayAuth">Two-Way Authentication</label>
                        <select id="twoWayAuth" name="twoWayAuth" className={`pif-input ${errors.twoWayAuth ? "is-invalid" : ""}`} value={formData.twoWayAuth} onChange={handleInputChange}>
                          <option value="Enabled">Enabled</option>
                          <option value="Disabled">Disabled</option>
                        </select>
                      </div>
                    </div>

                    {/* Practice Selection */}
                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Practice Assignment</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group" style={{ position: 'relative' }} ref={practiceDropdownRef}>
                        <label>Practice {!isViewMode && <span className="text-red-500">*</span>}</label>
                        <div
                          className={`practice-select-trigger${isViewMode ? ' practice-select-trigger--readonly' : ''}`}
                          onClick={() => !isViewMode && setShowPracticeDropdown(true)}
                        >
                          {selectedPractices.length > 0 && (
                            selectedPractices.map(practice => (
                              <span key={practice.practiceId} className="practice-tag">
                                {practice.practiceName}
                                {!isViewMode && selectedPractices.length > 1 && (
                                  <button
                                    type="button"
                                    className="practice-tag-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemovePractice(practice.practiceId);
                                    }}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))
                          )}
                          {!isViewMode && (
                            <input
                              type="text"
                              className="practice-select-input"
                              placeholder="Click to select practices..."
                              value={practiceSearchTerm}
                              onFocus={() => setShowPracticeDropdown(true)}
                              onChange={(e) => {
                                setPracticeSearchTerm(e.target.value);
                                setShowPracticeDropdown(true);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          {isViewMode && selectedPractices.length === 0 && (
                            <span className="practice-select-readonly-empty">None</span>
                          )}
                        </div>

                        {!isViewMode && showPracticeDropdown && (
                          <div className="practice-dropdown">
                            {loadingPractices ? (
                              <div className="practice-dropdown-empty">Loading practices...</div>
                            ) : practices.length === 0 ? (
                              <div className="practice-dropdown-empty">No practices found</div>
                            ) : practices
                              .filter(p => !selectedPractices.find(sp => sp.practiceId === p.id))
                              .filter(practice =>
                                matchesWordStart(
                                  practice.practice_name || `Practice ${practice.id}`,
                                  practiceSearchTerm
                                )
                              ).length === 0 ? (
                              <div className="practice-dropdown-empty">No matching practices</div>
                              ) : (
                              practices
                                .filter(p => !selectedPractices.find(sp => sp.practiceId === p.id))
                                .filter(practice =>
                                  matchesWordStart(
                                    practice.practice_name || `Practice ${practice.id}`,
                                    practiceSearchTerm
                                  )
                                )
                                .map(practice => (
                                  <div
                                    key={practice.id}
                                    className="practice-dropdown-item"
                                    onClick={() => handlePracticeClick(practice)}
                                  >
                                    {practice.practice_name || `Practice ${practice.id}`}
                                  </div>
                                ))
                            )}
                          </div>
                        )}
                        {errors.practices && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.practices}</span>}
                      </div>
                    </div>

                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <div className="pif-footer">
            {editId ? (
              <button
                type="button"
                className="pif-events-log-btn"
                onClick={() => setShowEventLogsModal(true)}
                title="View System Caregiver Event Logs"
              >
                <FontAwesomeIcon icon={faList} className="me-2" />
                Event Logs
              </button>
            ) : (
              <span />
            )}
            {!isViewMode && (
              <button type="button" className="pif-save-btn" disabled={isSubmitting || (isEditMode && !isChanged)} onClick={handleSubmit}>
                <Save size={18} />
                {isSubmitting ? 'Saving...' : (isEditMode ? 'Update System Caregiver' : 'Save System Caregiver')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showEventLogsModal && editId && (
        <div ref={logOverlayRef} className="practices-list-modal-overlay" style={{ zIndex: 110000 }}>
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">
                {formData.firstName || formData.lastName
                  ? `${formData.firstName} ${formData.lastName} — Event Logs`
                  : 'System Caregiver Event Logs'}
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowEventLogsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection moduleId="System Caregiver" targetUserId={editId} />
            </div>
          </div>
        </div>
      )}

      {/* Validation Warning Modal */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered className="add-system-caregiver-validation-modal">
        <Modal.Body className="add-system-caregiver-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-system-caregiver-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <Info size={40} color="#1A252F" />
          </div>
          <h4 className="add-system-caregiver-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button type="button" className="add-system-caregiver-modal-btn" onClick={() => setShowValidationModal(false)} style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}>
            OK
          </button>
        </Modal.Body>
      </Modal>

      <Modal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        centered
        dialogClassName="add-caregiver-confirm-dialog"
        contentClassName="add-caregiver-confirm-content"
      >
        <Modal.Body className="add-caregiver-confirm-body text-center">
          {/* <div className="add-caregiver-confirm-icon-wrapper mx-auto">
            <FontAwesomeIcon icon={faInfoCircle} />
          </div> */}
          <h4 className="add-caregiver-confirm-title">Are you sure?</h4>
          <p className="add-caregiver-confirm-text">
            Remove access to this system caregiver and reassign assigned patients.
          </p>

          <div className="add-caregiver-confirm-actions">
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--ghost"
              onClick={() => setShowConfirmationModal(false)}
            >
              No
            </button>
            <button
              type="button"
              className="add-caregiver-confirm-btn add-caregiver-confirm-btn--primary"
              onClick={startInactivationFlow}
            >
              Yes
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <ReassignmentModal
        show={showReassignmentModal}
        onClose={() => {
          setShowReassignmentModal(false);
          setFormData((prev) => ({ ...prev, status: 'Active' }));
        }}
        title="Reassign Patients"
        description={`Select a new system caregiver for each of the ${assignedPatients.length} patient(s) before inactivation.`}
        patients={assignedPatients}
        alternatives={alternativeCaregivers}
        reassignments={reassignments}
        onReassignmentChange={(patientId, systemCaregiverId) =>
          setReassignments((prev) => ({ ...prev, [patientId]: systemCaregiverId }))
        }
        onAssignAll={(systemCaregiverId) =>
          setReassignments(buildReassignmentsForAll(assignedPatients, systemCaregiverId))
        }
        onSave={handleBatchReassign}
        isSaving={isReassigning}
        saveLabel="Save & Inactivate"
        optionPlaceholder="Select system caregiver..."
        currentLabel="Current"
        reassignToLabel="Reassign to system caregiver"
        bulkAssignLabel="Assign all to"
        getPatientName={getSystemCaregiverPatientName}
        getCurrentAssignee={() => getCurrentSystemCaregiverDisplayName()}
        getAlternativeName={(item) =>
          `${item?.last_name || ''}, ${item?.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'
        }
      />

      {/* Patient Selection Modal */}
      {showPatientModal && selectedPracticeForModal && (
        <div
          className="patient-modal-overlay"
          onClick={() => {
            setShowPatientModal(false);
            setSelectedPracticeForModal(null);
            setSelectedPatientIds([]);
            setPracticePatients([]);
          }}
        >
          <div className="patient-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="patient-modal-header">
              <h5>
                {loadingPatients ? (
                  <span className="patient-modal-heading-skeleton patient-modal-skeleton-shimmer" />
                ) : (
                  <>Select Patients — {selectedPracticeForModal.practice_name || `Practice ${selectedPracticeForModal.id}`}</>
                )}
              </h5>
              <button
                type="button"
                className="patient-modal-close"
                onClick={() => {
                  setShowPatientModal(false);
                  setSelectedPracticeForModal(null);
                  setSelectedPatientIds([]);
                  setPracticePatients([]);
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="patient-modal-body">
              {loadingPatients ? (
                <div className="patient-table-skeleton-wrap">
                  <div className="patient-table-skeleton-header">
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer medium" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                  </div>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div className="patient-table-skeleton-row" key={idx}>
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer medium" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                    </div>
                  ))}
                </div>
              ) : practicePatients.length === 0 ? (
                <div className="patient-modal-empty">No patients found for this practice</div>
              ) : (
                <table className="patient-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Patient Name</th>
                      <th style={{ width: '70px' }}>Status</th>
                      <th>Caregiver</th>
                      <th className="patient-table-select-col">
                        <label className="select-all-label">
                          <input
                            type="checkbox"
                            checked={selectedPatientIds.length === practicePatients.length && practicePatients.length > 0}
                            onChange={handleSelectAllPatients}
                          />
                          <span>All</span>
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {practicePatients.map((patient, index) => {
                      const patientName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.name || 'N/A';
                      const caregiverName =
                        patient.caregiver_name ||
                        patient.caregiver ||
                        (patient.caregiver_first_name && patient.caregiver_last_name
                          ? `${patient.caregiver_first_name} ${patient.caregiver_last_name}`
                          : 'N/A');
                      const rawStatus = patient.status;
                      let statusLabel = 'Inactive';
                      let isActive = false;
                      if (rawStatus === 2 || rawStatus === '2' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'active')) {
                        statusLabel = 'Active';
                        isActive = true;
                      } else if (rawStatus === 3 || rawStatus === '3' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'pending')) {
                        statusLabel = 'Pending';
                      } else if (rawStatus === 4 || rawStatus === '4' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'locked')) {
                        statusLabel = 'Locked';
                      }
                      const isSelected = selectedPatientIds.includes(patient.id);

                      return (
                        <tr
                          key={patient.id}
                          className={isSelected ? 'selected' : ''}
                          onClick={() => handlePatientToggle(patient.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{index + 1}</td>
                          <td>{patientName}</td>
                          <td>
                            <span className={`patient-status-badge ${isActive ? 'active' : statusLabel === 'Pending' ? 'pending' : 'inactive'}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td>{caregiverName}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handlePatientToggle(patient.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="patient-modal-footer">
              <button
                type="button"
                className="btn-modal-close"
                onClick={() => {
                  setShowPatientModal(false);
                  setSelectedPracticeForModal(null);
                  setSelectedPatientIds([]);
                  setPracticePatients([]);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-modal-save"
                onClick={handleSavePracticeSelection}
                disabled={practicePatients.length > 0 && selectedPatientIds.length === 0}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      )}


    </>
  );
};


export default AddSystemCaregiverModal;


export { AddSystemCaregiverModal };